import { cloneDeep } from 'lodash-es';
import moment from 'moment';

type TAnyDay = {
  date?: string;
  events?: number;
  event_list?: TAnyEventBlock[];
};

type TAnyEventBlock = {
  events?: number;
  event_record?: any[];
};

const recordMergeKey = (record: any): string => {
  if (!record || typeof record !== 'object') {
    return '';
  }
  const id = record.id;
  if (id !== undefined && id !== null && id !== '' && Number(id) !== 0) {
    return `id:${String(id)}`;
  }
  const type = String(record.event_type ?? '').trim();
  const en = String(record.event_title_en ?? '').trim();
  const cn = String(record.event_title_cn ?? '').trim();
  const country = String(record.country ?? '').trim();
  const platform = String(record.platform ?? '').trim();
  return `t:${type}|${en}|${cn}|${country}|${platform}`;
};

const splitConsecutiveYmdRanges = (sortedYmd: string[]): string[][] => {
  if (sortedYmd.length === 0) {
    return [];
  }
  const out: string[][] = [];
  let cur: string[] = [sortedYmd[0]];
  for (let i = 1; i < sortedYmd.length; i++) {
    const prev = sortedYmd[i - 1];
    const d = sortedYmd[i];
    const diff = moment(d, 'YYYY-MM-DD').diff(moment(prev, 'YYYY-MM-DD'), 'days');
    if (diff === 1) {
      cur.push(d);
    } else {
      out.push(cur);
      cur = [d];
    }
  }
  out.push(cur);
  return out;
};

const findFirstRecordInDay = (
  day: TAnyDay,
  mergeKey: string,
): { record: any; block: TAnyEventBlock } | null => {
  for (const block of day.event_list || []) {
    for (const r of block.event_record || []) {
      if (recordMergeKey(r) === mergeKey) {
        return { record: r, block };
      }
    }
  }
  return null;
};

const ymdRangeInclusive = (startYmd: string, endYmd: string): string[] => {
  const out: string[] = [];
  const cur = moment(startYmd, 'YYYY-MM-DD');
  const end = moment(endYmd, 'YYYY-MM-DD');
  if (!cur.isValid() || !end.isValid()) {
    return out;
  }
  while (cur.isSameOrBefore(end, 'day')) {
    out.push(cur.format('YYYY-MM-DD'));
    cur.add(1, 'day');
  }
  return out;
};

const stripKeyFromDay = (day: TAnyDay, mergeKey: string): void => {
  if (!Array.isArray(day.event_list)) {
    return;
  }
  day.event_list.forEach(block => {
    if (!Array.isArray(block.event_record)) {
      return;
    }
    block.event_record = block.event_record.filter(
      (r: any) => recordMergeKey(r) !== mergeKey,
    );
  });
};

const pruneEmptyBlocks = (day: TAnyDay): void => {
  if (!Array.isArray(day.event_list)) {
    return;
  }
  day.event_list = day.event_list.filter(
    b => Array.isArray(b.event_record) && b.event_record.length > 0,
  );
};

const recalculateDayCounts = (day: TAnyDay): void => {
  if (!Array.isArray(day.event_list)) {
    day.events = 0;
    return;
  }
  let total = 0;
  day.event_list.forEach(block => {
    const n = Array.isArray(block.event_record) ? block.event_record.length : 0;
    block.events = n;
    total += n;
  });
  day.events = total;
};

/**
 * trend_event_list 等按日返回时，同一事件（优先 id，否则类型+标题+国家+平台）在连续自然日重复出现：
 * 合并为一条，start_time / end_time 取该连续段首日与末日，并写回 event_date 文案。
 */
export const mergeTrendEventListConsecutiveDays = (
  list: unknown[] | undefined | null,
): any[] => {
  if (!Array.isArray(list) || list.length === 0) {
    return list as any[];
  }

  const cloned = cloneDeep(list) as TAnyDay[];

  const dayByYmd = new Map<string, TAnyDay>();
  cloned.forEach(d => {
    const y = String(d?.date ?? '').trim();
    if (y) {
      dayByYmd.set(y, d);
    }
  });

  const keyToDates = new Map<string, string[]>();

  cloned.forEach(day => {
    const ymd = String(day?.date ?? '').trim();
    if (!ymd) {
      return;
    }
    const seenInDay = new Set<string>();
    day.event_list?.forEach(block => {
      block.event_record?.forEach((r: any) => {
        const k = recordMergeKey(r);
        if (!k) {
          return;
        }
        const dedupe = `${ymd}__${k}`;
        if (seenInDay.has(dedupe)) {
          return;
        }
        seenInDay.add(dedupe);
        if (!keyToDates.has(k)) {
          keyToDates.set(k, []);
        }
        const arr = keyToDates.get(k)!;
        if (!arr.includes(ymd)) {
          arr.push(ymd);
        }
      });
    });
  });

  keyToDates.forEach(dates => {
    dates.sort(
      (a, b) =>
        moment(a, 'YYYY-MM-DD').valueOf() - moment(b, 'YYYY-MM-DD').valueOf(),
    );
  });

  keyToDates.forEach((dates, mergeKey) => {
    const ranges = splitConsecutiveYmdRanges(dates);
    ranges.forEach(range => {
      if (range.length < 2) {
        return;
      }
      const startYmd = range[0];
      const endYmd = range[range.length - 1];
      const startDay = dayByYmd.get(startYmd);
      if (!startDay) {
        return;
      }
      const found = findFirstRecordInDay(startDay, mergeKey);
      if (!found) {
        return;
      }

      const merged = {
        ...cloneDeep(found.record),
        start_time: startYmd,
        end_time: endYmd,
        event_date: `${startYmd} 00:00:00 ~ ${endYmd} 00:00:00`,
      };

      ymdRangeInclusive(startYmd, endYmd).forEach(ymd => {
        const d = dayByYmd.get(ymd);
        if (d) {
          stripKeyFromDay(d, mergeKey);
        }
      });

      if (!Array.isArray(found.block.event_record)) {
        found.block.event_record = [];
      }
      found.block.event_record.push(merged);
    });
  });

  cloned.forEach(d => {
    pruneEmptyBlocks(d);
    recalculateDayCounts(d);
  });

  return cloned;
};
